import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { CreditScoreChart } from './CreditScoreChart';
import type { FinancialHealthStats } from '../../types/financialStats';
import CreditScoreCustomHeader from './CreditScoreCustomHeader';
import { useChartDrag } from './ChartDragUtils';
import { useEvidenceModal } from '@/context/EvidenceContext';

interface CreditScoreChartWrapperProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  monthsToShow?: number;
  onExpandClick?: React.MutableRefObject<any>;
  /** BUG-UI-071A: Run ID for evidence modal */
  runId?: string;
  /** BUG-UI-071A: Member ID for historical evidence */
  memberId?: string;
  /** BUG-UI-071A: Member name for modal display */
  memberName?: string;
  /** Authoritative credit score (member.credit_score) forwarded to the chart. */
  overrideCreditScore?: number | null;
}

// Extract the main wrapper to reuse the logic for showing a custom header
export const CreditScoreChartWrapper: React.FC<CreditScoreChartWrapperProps> = (props) => {
  const { data, showDebug, onExpandClick, monthsToShow = 12, runId, memberId, memberName, overrideCreditScore } = props;

  // BUG-UI-071A: Use evidence modal instead of ChartExpandModal
  const { showEvidence } = useEvidenceModal();

  // BUG-UI-071A: Open evidence modal with 4-tab structure
  const openEvidenceModal = useCallback(() => {
    if (runId) {
      console.log('Opening EvidenceModal from CreditScoreChartWrapper');
      showEvidence({
        uiFieldPath: 'lender.current_finances.credit_score',
        runId,
        memberId,
        disclosureLevel: 'LENDER',
        memberName,
      });
    } else {
      console.warn('CreditScoreChartWrapper: Cannot open evidence modal - no runId provided');
    }
  }, [runId, memberId, memberName, showEvidence]);

  // BUG-UI-071A: Expose the openModal function through the onExpandClick prop
  useEffect(() => {
    if (onExpandClick) {
      // @ts-ignore
      onExpandClick.current = onExpandClick.current || {};
      // @ts-ignore
      onExpandClick.current.openModal = openEvidenceModal;
    }
  }, [onExpandClick, openEvidenceModal]);
  
  // Get credit scores for each 3-month period (similar to CreditScoreChart)
  const creditScoreData = useMemo(() => {
    // Sort data chronologically
    const sortedData = [...data].sort(
      (a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    return sortedData;
  }, [data]);
  
  // Initial state for visible data
  const [visibleData, setVisibleData] = useState<FinancialHealthStats[]>(() => {
    if (!creditScoreData.length) return [];
    return creditScoreData.slice(-monthsToShow);
  });

  // Enhanced handleDataChange with animation management
  const handleDataChange = useCallback((newVisibleData: FinancialHealthStats[]) => {
    setVisibleData(newVisibleData);
  }, []);

  // Use the chart drag hook for consistent drag behavior
  const {
    viewWindow,
    goEarlier,
    goLater
  } = useChartDrag(creditScoreData, monthsToShow, handleDataChange);

  return (
    <div className="relative h-full">
      {/* Process debug info */}
      {process.env.NODE_ENV === 'development' && showDebug && (
        <div className="absolute top-0 right-0 bg-white/80 p-2 text-xs text-slate-500 z-10 rounded border">
          <div>Total scores: {creditScoreData.length}</div>
          <div>Visible: {visibleData.length}</div>
          <div>Window: {viewWindow.start} - {viewWindow.end}</div>
        </div>
      )}
      
      {/* Custom Chart header for Credit Score */}
      <CreditScoreCustomHeader
        viewWindow={viewWindow}
        totalMonths={creditScoreData.length}
        monthsToShow={monthsToShow}
        onEarlierClick={goEarlier}
        onLaterClick={goLater}
      />
      
      {/* Pass only visible data to the actual chart component */}
      <div className="pt-0 h-[calc(100%-32px)]">
        <CreditScoreChart
          data={creditScoreData}
          showDebug={false}
          onExpandClick={onExpandClick}
          monthsToShow={monthsToShow}
          hideHeader={true}
          overrideCreditScore={overrideCreditScore}
        />
      </div>
    </div>
  );
}; 